import React, { MouseEventHandler, forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { IFundSection } from "./SecondaryNav.types";
import { useInViewObserver } from "./useInViewObserver";
import "./secondaryNav.scss";
import { jumpToSection } from "./jumpToSection";
import { getCleanPageURL } from "./setSecondaryNavState";
import HTMLReactParser from "html-react-parser";

export interface ISecondaryNav extends ComponentInterface { }

//Handle bots
const isCrawler = /bot|googlebot|crawler|spider|robot|crawling/i.test(navigator.userAgent);
//Whether or not to render <a> tags around secondary nav items. Might be needed for Crawlers.
const addLinksToSecondaryNav = false;

export interface HeaderHeightEvent extends Event {
    detail?: {
        headerHeight: number | undefined,
        cumilativeAlertBarsHeight: number | undefined
    }
}

const headerHeightEventHandler = (e: HeaderHeightEvent) => {
    if (!e.detail) return;
    let secondayNavOffsetTop = (e.detail.headerHeight ?? 0) + (e.detail.cumilativeAlertBarsHeight ?? 0);
    return secondayNavOffsetTop;
}

//For when the fund launch promo-banner is there on the page.
const stickySecondaryNavScrollHandler = (e: Event, setFixedClass: React.Dispatch<React.SetStateAction<string>>) => {
    const fundPageBody = document.querySelector(".fund-details-body-wrp");
    const stickyHeaderHeight = document.querySelector("#sticky-site-header")?.getBoundingClientRect().height ?? 0;

    //see if fundpage body is under the sticky header.
    if (fundPageBody && fundPageBody.getBoundingClientRect().top < stickyHeaderHeight + 75) {
        setFixedClass("");
    } else {
        setFixedClass("unfixed");
    }
}

export default function SecondaryNav(props: ISecondaryNav) {
    const element = props.rootElement;
    element.classList.remove("d-none");
    const [fixedClass, setFixedClass] = useState("");
    const [FundSections, setFundSections] = useState(getFundSections());
    const cleanURL = getCleanPageURL(FundSections);
    const [topOffset, setTopOffset] = useState(0);
    const stickyHeaderRef = useRef<HTMLDivElement>(null);
    const sidebarRef = useRef<HTMLElement>(null);
    const placeholderRef = useRef<HTMLDivElement>(null);
    const isStuckRef = useRef(false);

    useEffect(() => {
        const updateStickyTop = () => {
            if (stickyHeaderRef.current) {
                const rect = stickyHeaderRef.current.getBoundingClientRect();
                const headerHeight = rect.height;

                document.documentElement.style.setProperty('--sticky-header-height', `${headerHeight}px`);
            }
        };

        const resizeObserver = new ResizeObserver(updateStickyTop);

        if (stickyHeaderRef.current) {
            resizeObserver.observe(stickyHeaderRef.current);
        }

        updateStickyTop();

        window.addEventListener('resize', updateStickyTop);
        window.addEventListener('scroll', updateStickyTop);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateStickyTop);
            window.removeEventListener('scroll', updateStickyTop);
        };
    }, []);

    useEffect(() => {
        const checkIfStuck = () => {
            const sidebar = sidebarRef.current;
            const placeholder = placeholderRef.current;
            const stickyHeader = document.querySelector('.sticky-header');

            if (sidebar && placeholder && stickyHeader) {
                const headerRect = stickyHeader.getBoundingClientRect();

                const elementToCheck = isStuckRef.current ? placeholder : sidebar;
                const elementRect = elementToCheck.getBoundingClientRect();

                const shouldBeFixed = elementRect.top <= headerRect.bottom;

                if (shouldBeFixed && !isStuckRef.current) {
                    placeholder.style.height = `${sidebar.offsetHeight}px`;
                    placeholder.style.display = 'block';
                    sidebar.classList.add('fixed');
                    isStuckRef.current = true;
                } else if (!shouldBeFixed && isStuckRef.current) {
                    placeholder.style.display = 'none';
                    sidebar.classList.remove('fixed');
                    isStuckRef.current = false;
                }
            }
        };

        let ticking = false;
        const handleScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(() => {
                    checkIfStuck();
                    ticking = false;
                });
                ticking = true;
            }
        };

        checkIfStuck();
        window.addEventListener('scroll', handleScroll);
        window.addEventListener('resize', checkIfStuck);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', checkIfStuck);
        };
    }, []);

    useEffect(() => {
        const fundHeader = document.querySelector('.fund-page-header-wrp') as HTMLElement;
        const navWrapper = $('.fund-page-secondary-nav-wrp');
        const navInner = $(".fund-page-secondary-nav-wrp > div");

        const VISIBLE_THRESHOLD = 0.4;
        const HYSTERESIS = 10;
        let isFixed = false;
        let rafId: number;

        const updateNavState = () => {
            if (!fundHeader) return;
            const rect = fundHeader.getBoundingClientRect();
            const totalHeight = fundHeader.offsetHeight;

            const visibleHeight = Math.max(0, Math.min(rect.bottom, totalHeight));
            const visibleRatio = totalHeight > 0 ? visibleHeight / totalHeight : 0;

            const isBelowViewport = rect.top > 0;

            const shouldBeFixed = !isBelowViewport && visibleRatio < VISIBLE_THRESHOLD - (HYSTERESIS / totalHeight);
            const shouldBeUnfixed = isBelowViewport || visibleRatio > VISIBLE_THRESHOLD + (HYSTERESIS / totalHeight);

            if (shouldBeFixed && !isFixed) {
                isFixed = true;
                navWrapper.addClass("fixed-nav");
                navInner.addClass("container");
            } else if (shouldBeUnfixed && isFixed) {
                isFixed = false;
                navWrapper.removeClass("fixed-nav");
                navInner.removeClass("container");
            }
        };

        const handleScroll = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(updateNavState);
        };

        updateNavState();

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('resize', handleScroll, { passive: true });
        window.addEventListener('orientationchange', handleScroll);

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleScroll);
            window.removeEventListener('orientationchange', handleScroll);
        };
    }, []);

    useSectionJumpOnURL(FundSections);
    useInViewObserver(FundSections, setFundSections);

    useEffect(() => {
        const activeItem = document.querySelector('.sidebar-nav__item.active');

        if (activeItem) {
            activeItem.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center'
            });
        }
    }, [FundSections]);

    useEffect(() => {
        document.addEventListener("headerHeightEvent", (e) => {
            let offSet = headerHeightEventHandler(e) ?? topOffset;
            offSet += 50;
            setTopOffset(offSet);
            document.documentElement.style.setProperty('--secondary-nav-offset', offSet + 'px');
        });
    }, []);

    useEffect(() => {
        if (!document.querySelector(".fund-banner-wrapper .desktop-content")) return;
        setFixedClass("unfixed");
        window.addEventListener("scroll", (e) => stickySecondaryNavScrollHandler(e, setFixedClass));
        return () => {
            window.removeEventListener("scroll", (e) => stickySecondaryNavScrollHandler(e, setFixedClass));
        }
    }, [])

    useEffect(() => {
        if (element?.parentElement) {
            element.parentElement.style.minHeight = '0';
        }
    }, [element]);

    return (
        <>
            <StickyHeader ref={stickyHeaderRef} FundSections={FundSections} />
            <div ref={placeholderRef} style={{ display: 'none' }} />
            <aside
                ref={sidebarRef}
                className={`sticky-left-sidebar ${fixedClass} nav-trans`}
                aria-label="Jump to"
            >
                <nav className="sidebar-nav-wrapper">
                    <ul className="sidebar-nav container">
                        {FundSections &&
                            FundSections.map((FundSection) => (
                                <SecondaryNavItem
                                    key={FundSection.id as React.Key}
                                    FundSection={FundSection}
                                    displayClass="sidebar-nav__item"
                                    cleanURL={cleanURL}
                                ></SecondaryNavItem>
                            ))}
                    </ul>
                </nav>
            </aside>
        </>
    );
}

const StickyHeader = forwardRef<HTMLDivElement, { FundSections: IFundSection[] }>(
    ({ FundSections }, ref) => {
        const ticker = useMemo(() => document.querySelector(".fund-title-wrapper div.ticker")?.innerHTML ?? "", []);
        const fundName = useMemo(() => document.getElementsByTagName("ve-fundname")[0]?.textContent?.trim() ?? "", []);
        const activeSectionLabel = FundSections.find((x) => x.activeClass == "active")?.name ?? "";
        const [secondaryNavTop, setsecondaryNavTop] = useState(0);
        const cleanURL = getCleanPageURL(FundSections);
        const isMutualFund = useMemo(() => document.getElementsByClassName("mutual-fund-page")[0] ? true : false, []);
        const [researchLinksHTML, setResearchLinksHTML] = useState<string>("");
        const [overviewLinksHTML, setOverviewLinksHTML] = useState<string>("");

        /**
         * The alertbar code will dispatch an event to document whenever the alertbars are added or removed from the page.
         * We need to listen to these events and adjust the offset of our sticky nav accordingly.
         * Refer generic-promo-banner.js for details of dispatcher.
         */

        useEffect(() => {
            const defaultHeaderHeight = document.querySelector(".root .sticky-wrapper")?.getBoundingClientRect().height ?? 0;
            setsecondaryNavTop(defaultHeaderHeight);
            document.addEventListener("headerHeightEvent", (e) => {
                let offSet = headerHeightEventHandler(e) ?? secondaryNavTop;
                setsecondaryNavTop(offSet);
            });
        }, []);

        useEffect(() => {
            const researchLinks = document.querySelector(".research_links");
            if (researchLinks) {
                setResearchLinksHTML(researchLinks.parentElement?.innerHTML ?? "");
            }

            const overviewSection = document.querySelector(".overview-link-section .content-area-right-bottom-part");
            if (overviewSection) {
                const clonedSection = overviewSection.cloneNode(true) as HTMLElement;
                const scripts = clonedSection.querySelectorAll("script");
                scripts.forEach(script => script.remove());
                setOverviewLinksHTML(clonedSection.innerHTML);
            }
        }, []);

        return (
            <div ref={ref} className="sticky-header" style={{ top: 'var(--header-height)' }}>
                <div className="container">
                    <div className="inner-container">
                        <FundLabelContent ticker={ticker} fundName={fundName} />
                        {/* <Breadcrumbs /> */}
                        {researchLinksHTML && (
                            <div className="research_links__wrapper">
                                {HTMLReactParser(researchLinksHTML)}
                            </div>
                        )}

                        {overviewLinksHTML && (
                            <div className="overview_links__wrapper" onClick={(e) => handleOverviewLinksClick(e)}>
                                {HTMLReactParser(overviewLinksHTML)}
                            </div>
                        )}
                    </div>
                    <div className="inner-container-mobile">
                        <FundLabelContent ticker={ticker} fundName={fundName} isMutualFund={isMutualFund} />
                        {/* <Breadcrumbs /> */}
                        <MobileButtonAndDropdown FundSections={FundSections} cleanURL={cleanURL} activeSectionLabel={activeSectionLabel as string} />
                    </div>
                </div>
            </div>
        );
    });

//declare jQuery bootstrap;
declare global {
    interface Window { $: Function | any }
}

function MobileButtonAndDropdown({ FundSections, cleanURL, activeSectionLabel }: { FundSections: IFundSection[], cleanURL: string, activeSectionLabel: string }) {
    const [mobileDropdownHTML, setMobileDropdownHTML] = useState<string>("");

    useEffect(() => {
        const mobileDropdown = document.querySelector(".research_links__mobile.dropdown");
        if (mobileDropdown) {
            setMobileDropdownHTML(mobileDropdown.outerHTML);
        }
    }, []);

    return mobileDropdownHTML ? (
        <div className="mobile-secondary-nav-wrapper">{HTMLReactParser(mobileDropdownHTML)}</div>
    ) : null;
}

function FundLabelContent({ ticker, fundName, isMutualFund }: { ticker: string; fundName: string, isMutualFund?: boolean }) {
    var dropdown = document.getElementsByClassName("mf-fund-selector")[0];
    useEffect(() => {
        if (dropdown) {
            var wrapperDiv = document.getElementsByClassName("mf-dropdown-div")[0];
            wrapperDiv.innerHTML = dropdown.outerHTML
        }
    }, [])
    if (isMutualFund) {
        return (
            <div className="fund-label d-flex align-items-center my-2">
                <div className="fund-name-label px-2">{fundName}</div>
                <div className="ticker-label px-2 font-weight-bold">{HTMLReactParser(ticker)}</div>
                <div className="mf-dropdown-div"></div>
            </div>
        )
    }
    else return (
        <div className="fund-label d-flex">
            <div className="ticker-label pr-3 font-weight-bold">{HTMLReactParser(ticker)}</div>
            <div className="fund-name-label px-2">{fundName} </div>
        </div>
    );
}

export function Breadcrumbs() {
    const pageBreadcrumb = document.querySelectorAll(".fund-breadcrumb-wrp");
    if (pageBreadcrumb.length === 0) return <></>;
    const breadcrumbMarkup = pageBreadcrumb[0].innerHTML;
    return <div className="fund-breadcrumb-wrp laptop-only">{HTMLReactParser(breadcrumbMarkup)}</div>
}

export function getFundSections(): IFundSection[] {
    const FundPageSectionNodes = document.querySelectorAll("section.fund-page-section");
    return Array.prototype.map.call(FundPageSectionNodes, function (sectionNode: Element): IFundSection {
        return {
            id: sectionNode.id,
            name: sectionNode.querySelector("h2.fund-page-section-header")?.getAttribute("data-section-name") ?? null,
            activeClass: "",
        };
    }) as IFundSection[];
}

const clickListenerForSeondaryNavAnchors: MouseEventHandler<HTMLAnchorElement> = (e) => !isCrawler && e.preventDefault();

function SecondaryNavItem({ FundSection, displayClass, cleanURL }: { FundSection: IFundSection; displayClass: string, cleanURL: string }) {
    const { name, id, activeClass } = FundSection;
    const url = `${cleanURL}/${id}/`;

    return (
        <li className={displayClass + " " + activeClass} data-target={id} onClick={handleClick}>
            {addLinksToSecondaryNav ?
                <a href={url} onClick={clickListenerForSeondaryNavAnchors} className="secondary-nav-link">{name}</a> : <>{name}</>}
        </li>
    );
}

function handleClick(event: React.MouseEvent<HTMLElement>) {
    if (isCrawler) return true;
    const currentTarget = event.currentTarget;
    const targetedElementId = currentTarget.dataset.target ?? "";
    jumpToSection(targetedElementId);
}

function handleOverviewLinksClick(e: React.MouseEvent<HTMLDivElement>) {
    const target = e.target as HTMLElement;
    const link = target.closest("a");

    if (!link) return;

    if (link.classList.contains("id-how-to-buy-etf-modal")) {
        e.preventDefault();

        const modal = document.getElementById("id-how-to-buy-etf-modal");
        if (modal && window.$) {
            window.$(modal).modal("show");
        }
    }
}

function useSectionJumpOnURL(FundSections: IFundSection[]) {
    useEffect(() => {
        try {
            const urlPath = window.location.pathname.split("/").reverse();
            const lastPathToken = urlPath[0] == "" ? urlPath[1] : urlPath[0];
            const targetSection = FundSections.find((x) => x.id == lastPathToken);
            const targetSectionId = targetSection?.id;
            if (!targetSectionId) return;
            if (targetSection == FundSections[0]) return;
            window.setTimeout(() => {
                window.scrollY = 0;
                jumpToSection(targetSectionId as string)
            }, 600);
        } catch (error) {
            console.log(error);
        }
    }, []);
}